'use client';

import { useEffect, useRef, useCallback } from 'react';
import { createPerformanceMonitor } from '@/lib/performance-monitor';
import { getDebugger } from '@/lib/debug-performance';

export function usePerformanceTracking(pageType: string, pageName: string) {
  const monitor = useRef<ReturnType<typeof createPerformanceMonitor> | undefined>(undefined);
  const renderStart = useRef(performance.now());
  const hasTrackedMount = useRef(false);

  useEffect(() => {
    if (!monitor.current) {
      monitor.current = createPerformanceMonitor(pageType, pageName);
    }

    // Track component mount time
    if (!hasTrackedMount.current) {
      hasTrackedMount.current = true;
      const mountTime = performance.now() - renderStart.current;
      const debug = getDebugger(pageType);
      debug.log('COMPONENT_RENDER_TO_MOUNT', `Component rendered and mounted in ${mountTime.toFixed(2)}ms`);
      
      // Track real navigation metrics
      monitor.current.trackRealNavigation();
    }
  }, [pageType, pageName]);

  const trackOperation = useCallback((operationName: string) => {
    const startTime = performance.now();
    return {
      end: (details?: string) => {
        const duration = performance.now() - startTime;
        const debug = getDebugger(pageType);
        debug.log(operationName, details || `${operationName} completed in ${duration.toFixed(2)}ms`);
      }
    };
  }, [pageType]);

  const trackAsync = useCallback(async <T,>(
    operationName: string,
    asyncFn: () => Promise<T>
  ): Promise<T> => {
    const startTime = performance.now();
    const debug = getDebugger(pageType);
    
    try {
      debug.log(`${operationName}_START`, `Starting ${operationName}`);
      const result = await asyncFn();
      const duration = performance.now() - startTime;
      debug.log(`${operationName}_COMPLETE`, `${operationName} completed in ${duration.toFixed(2)}ms`);
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      debug.log(`${operationName}_ERROR`, `${operationName} failed after ${duration.toFixed(2)}ms`);
      throw error;
    }
  }, [pageType]);

  return {
    trackOperation,
    trackAsync,
    monitor: monitor.current
  };
}